import classNames from 'classnames';
import {REACT_EVENT_KEY_ENTER} from '../../constants/eventConstants';

type Props = {
  children: React.ReactNode;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger' | 'safe' | 'inverted-secondary';
  id?: string;
  size?: 'small' | 'medium' | 'large';
  handleClick?: $TSFixMeFunction;
  trackEvent?: $TSFixMeFunction;
  handleKeyDown?: $TSFixMeFunction;
  leftIcon?: string;
  rightIcon?: string;
  customClassNames?: string;
  isCustomWidthApplied?: boolean;
};

/**
 * Renders the button component
 * @param {string} size - size of the button
 * @param {string} variant - the color palette of the button.
 * @param {string} id - optional unique component level id
 * @param {HTMLElement} children - button child component.
 * @param {boolean} disabled - flag to disable the button.
 * @param {Function} handleClick - The callback that handles the button click.
 * @param {Function} trackEvent - The callback that handles the track event of the button.
 * @param {string} customClassNames - Additional custom class names to apply to the button.
 * @param {boolean} isCustomWidthApplied - flag to apply custom width to the button when specific width is passed as part of customClassNames.
 * @returns {JSX.Element} Returns markup for a button
 */
function Button({
  size = 'medium',
  variant = 'primary',
  children,
  disabled = false,
  handleClick,
  trackEvent,
  id,
  handleKeyDown,
  leftIcon = '',
  rightIcon = '',
  customClassNames = '',
  isCustomWidthApplied = false,
}: Props) {
  /** handles the track event and the button click
   */
  const handler = (e: $TSFixMe) => {
    if (trackEvent) {
      trackEvent();
    }
    if (handleClick) {
      handleClick(e);
    }
  };

  const keyboardEventHandler = (e: $TSFixMe) => {
    if (e.key === REACT_EVENT_KEY_ENTER && handleKeyDown) {
      handleKeyDown(e);
    }
    return;
  };
  const buttonWidth = isCustomWidthApplied ? '' : 'sm:w-auto';

  const buttonClasses = {
    base: `rounded-oval self-center font-extrabold text-center border-2 w-full min-w-20 ${buttonWidth}`,
    disabled: 'bg-onSurface-light border-onSurface-light cursor-not-allowed text-onBackground-disabled',
    size: {
      small: 'px-3 py-1 text-sm',
      medium: 'px-4 py-2 text-base',
      large: 'px-6 py-3 text-lg',
    },
    variant: {
      primary: 'btn-focus-primary bg-brand text-onBrand-primary hover:bg-brandStateA border-onBrand-primary',
      secondary:
        'btn-focus-secondary text-onSurface-primary bg-surface hover:bg-onSurface-primary hover:text-surface border-onSurface-primary',
      'inverted-secondary':
        'btn-focus-surface text-surface bg-onSurface-primary hover:bg-surface hover:text-onSurface-primary border-onSurface-primary',
      danger: 'btn-focus-critical border-none text-onCritical-primary bg-critical',
      safe: 'btn-focus-success border-none text-onSuccess-primary bg-success',
    },
  };
  const buttonGroupClass = classNames(
    buttonClasses.size[size],
    buttonClasses.base,
    {
      [`${buttonClasses.disabled}`]: disabled,
      [`${buttonClasses.variant[variant]}`]: !disabled,
    },
    customClassNames
  );

  const baseIconClasses =
    'text-base font-bold border-b-2 border-solid material-icons group-hover:text-onBackground-primary';
  const leftIconClassString = `${baseIconClasses} mr-2`;
  const rightIconClassString = `${baseIconClasses} ml-2`;

  return (
    <button
      disabled={disabled}
      className={buttonGroupClass}
      onClick={handler}
      {...{id}}
      data-testid={id}
      onKeyDown={keyboardEventHandler}
    >
      {leftIcon && <i className={leftIconClassString}>{leftIcon}</i>}
      {children}
      {rightIcon && <i className={rightIconClassString}>{rightIcon}</i>}
    </button>
  );
}
export default Button;
